import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@mono/ui-components';
import { Archive, ArchiveRestore, FolderOpen, MoreVertical, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getProjectTypeLabel, getProjectTypeStyles } from '../../constants/project-types';
import { useArchiveProject, useDeleteProject, useUnarchiveProject } from '../../hooks/useProjects';
import { getProjectPath } from '../../lib/utils/project-routes';
export function ProjectCard({ project }) {
    const navigate = useNavigate();
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const archiveProject = useArchiveProject();
    const unarchiveProject = useUnarchiveProject();
    const deleteProject = useDeleteProject();
    const formattedDate = new Date(project.lastActivityAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
    const roleColors = {
        owner: 'default',
        member: 'secondary',
        view_only: 'outline'
    };
    const styles = getProjectTypeStyles(project.type);
    const isOwner = project.role === 'owner';
    const isArchived = project.status === 'archived';
    const handleArchiveClick = async ()=>{
        try {
            await archiveProject.mutateAsync(project.projectId);
            toast.success('Project archived successfully');
            setShowArchiveDialog(false);
        } catch (error) {
            // Use error.message verbatim — Connect throws ConnectError and REST
            // The fallback only kicks in if a non-Error value was thrown.
            toast.error(error instanceof Error ? error.message : 'Failed to archive project');
        }
    };
    const handleUnarchiveClick = async ()=>{
        try {
            await unarchiveProject.mutateAsync(project.projectId);
            toast.success('Project unarchived successfully');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to unarchive project');
        }
    };
    const handleDeleteClick = async ()=>{
        try {
            await deleteProject.mutateAsync(project.projectId);
            toast.success('Project deleted permanently');
            setShowDeleteDialog(false);
            navigate('/projects');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to delete project');
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "group relative bg-card rounded-xl border border-border shadow-sm hover:shadow-lg hover:border-border/80 transition-all duration-200 overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: `h-1 ${styles.bar}`
                    }),
                    /*#__PURE__*/ _jsxs(Link, {
                        to: getProjectPath(project),
                        className: "block p-5",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-start justify-between gap-3 mb-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3 min-w-0",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: `shrink-0 p-2 rounded-lg ${styles.iconBg}`,
                                                children: /*#__PURE__*/ _jsx(FolderOpen, {
                                                    className: `w-5 h-5 ${styles.icon}`
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "font-semibold text-base text-foreground truncate group-hover:text-foreground/80 transition-colors",
                                                children: project.name
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex items-center gap-2 shrink-0",
                                        children: /*#__PURE__*/ _jsx("span", {
                                            className: `text-xs px-2.5 py-1 rounded-full font-medium ${styles.badge}`,
                                            children: getProjectTypeLabel(project.type)
                                        })
                                    })
                                ]
                            }),
                            project.description && /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground line-clamp-2 mb-4 leading-relaxed",
                                children: project.description
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between pt-3 border-t border-border/50",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1.5 text-xs text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx(Users, {
                                                className: "w-4 h-4"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    project.memberCount,
                                                    " ",
                                                    project.memberCount === 1 ? 'member' : 'members'
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-muted-foreground",
                                                children: formattedDate
                                            }),
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: roleColors[project.role] ?? 'default',
                                                className: "text-xs",
                                                children: project.role.replace('_', ' ')
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    isOwner && /*#__PURE__*/ _jsx("div", {
                        className: "absolute top-3 right-3 z-10",
                        children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                            children: [
                                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                    asChild: true,
                                    children: /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        className: "p-1.5 rounded-md bg-white/90 hover:bg-white border border-border/60 hover:border-border shadow-sm opacity-70 hover:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity",
                                        onClick: (e)=>e.preventDefault(),
                                        "aria-label": "Project actions",
                                        disabled: archiveProject.isPending || unarchiveProject.isPending || deleteProject.isPending,
                                        children: /*#__PURE__*/ _jsx(MoreVertical, {
                                            className: "w-4 h-4 text-muted-foreground"
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                    align: "end",
                                    className: "w-48",
                                    children: [
                                        isArchived ? /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                            onClick: (e)=>{
                                                e.preventDefault();
                                                handleUnarchiveClick();
                                            },
                                            disabled: unarchiveProject.isPending,
                                            className: "cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx(ArchiveRestore, {
                                                    className: "w-4 h-4 mr-2"
                                                }),
                                                "Unarchive Project"
                                            ]
                                        }) : /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                            onClick: (e)=>{
                                                e.preventDefault();
                                                setShowArchiveDialog(true);
                                            },
                                            disabled: archiveProject.isPending,
                                            className: "cursor-pointer text-yellow-600 focus:text-yellow-700 focus:bg-yellow-50",
                                            children: [
                                                /*#__PURE__*/ _jsx(Archive, {
                                                    className: "w-4 h-4 mr-2"
                                                }),
                                                "Archive Project"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                                        /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                            onClick: (e)=>{
                                                e.preventDefault();
                                                setShowDeleteDialog(true);
                                            },
                                            disabled: deleteProject.isPending,
                                            className: "cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50",
                                            children: [
                                                /*#__PURE__*/ _jsx(Trash2, {
                                                    className: "w-4 h-4 mr-2"
                                                }),
                                                "Delete Permanently"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showArchiveDialog,
                onOpenChange: setShowArchiveDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Archive Project?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This will archive ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: project.name
                                        }),
                                        " and hide it from the main projects list. Files and chats will be preserved but editing will be disabled. You can restore it later from the archived projects list."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleArchiveClick,
                                    className: "bg-yellow-600 hover:bg-yellow-700",
                                    children: "Archive Project"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showDeleteDialog,
                onOpenChange: setShowDeleteDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete Project Permanently?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This will ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: "permanently delete"
                                        }),
                                        " ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: project.name
                                        }),
                                        " and all associated data including files, chats, and team members. This action cannot be undone."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleDeleteClick,
                                    className: "bg-red-600 hover:bg-red-700",
                                    children: "Delete Permanently"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
